/**
 * Browser + API connectivity state for offline UX (banner, auth copy, guards).
 */
import { reactive, computed } from 'vue';

const state = reactive({
  browserOffline: typeof navigator !== 'undefined' ? !navigator.onLine : false,
  apiUnreachable: false,
});

export function isNetworkError(error) {
  if (!error) return false;
  if (typeof navigator !== 'undefined' && !navigator.onLine) return true;

  const code = error.code;
  if (
    code === 'ERR_NETWORK' ||
    code === 'ECONNABORTED' ||
    code === 'auth/network-request-failed' ||
    code === 'unavailable'
  ) {
    return true;
  }
  if (error.message === 'Network Error') return true;
  if (error.request && !error.response) return true;
  return false;
}

export function initConnectivityListeners() {
  if (typeof window === 'undefined') return;

  const sync = () => {
    state.browserOffline = !navigator.onLine;
    if (navigator.onLine) {
      state.apiUnreachable = false;
    }
  };

  window.addEventListener('online', sync);
  window.addEventListener('offline', sync);
  sync();
}

export function markApiUnreachable(unreachable = true) {
  state.apiUnreachable = unreachable;
}

export function useConnectivity() {
  const isOffline = computed(() => state.browserOffline || state.apiUnreachable);
  const message = computed(() => {
    if (state.browserOffline) {
      return 'You’re offline. Charts and alerts won’t refresh until you’re back online.';
    }
    if (state.apiUnreachable) {
      return 'Can’t reach Coinproof right now. Check your connection and try again.';
    }
    return '';
  });

  return {
    isOffline,
    browserOffline: computed(() => state.browserOffline),
    apiUnreachable: computed(() => state.apiUnreachable),
    message,
    retry,
  };
}

export function retry() {
  if (typeof window === 'undefined') return;
  if (navigator.onLine) {
    state.apiUnreachable = false;
    window.location.reload();
  }
}
